import React, { useCallback, useMemo, useState } from 'react';
import {
    BasicDropdownMenu,
    BasicMenuItemProps,
    BodyText,
    Button,
    ChipValueType,
    ConfirmationDialogBox,
    Divider,
    Dropdown,
    FilterTaggedButton,
    Icon,
    IconButton,
    Modal,
    MultiSelectDropdownMenu,
    MultiSelectItemProps,
    TextAreaField,
    UnifiedSelectBox,
} from '@placer-ui/components';
import { TitleModalHeader } from 'features/my-zone/support-requests/components/support-requests-modals/components/title-modal-header/title-modal-header';
import styles from './xtra-report-share-modal.module.scss';
import type { OrderManagementRequest } from 'core/entities';
import { supportApi } from 'API/offline-reports-api';
import { useOfflineReportsActions } from 'store/offline-reports/use-offline-reports-actions';
import { selectOfflineReportsUserAccountInfo } from 'store/offline-reports/selectors/offline-reports-selectors-factory';
import { useSelector } from 'react-redux';
import { getShareMenuOptions } from './xtra-requests-table-shared';
import { tableDateFormatter } from 'utils/table-formatters/table-formatters';
import { createAccountMembersItems } from '../../utils/utils';
import { useGetUsersMap } from '../../hooks/use-get-xtra-requests-data';
import { useOpenSupportRequest } from 'features/my-zone/support-requests/components/support-requests-modals/support-modal-manager';
import { SHARE_SUCCESS_BUTTON_TEXT, SHARE_SUCCESS_TEXT, SHARE_SUCCESS_TITLE } from '../../consts';
import classNames from 'classnames';

export type XtraRequestShareModalProps = {
    selectedRequest: OrderManagementRequest;
};

export const XtraRequestShareModal = ({ selectedRequest }: XtraRequestShareModalProps) => {
    const { id, reportName, reportDates, sharedWith, target } = selectedRequest;

    const accountShared = (target && target === 'account') || false;
    const { closeSupportRequest } = useOpenSupportRequest();
    const [currentShareList, setCurrentShareList] = useState<string[]>(
        sharedWith?.[0] === '' ? [] : (sharedWith as string[]),
    );
    const usersMap = useGetUsersMap();
    const [query, setQuery] = useState<string>('');
    const [shareRequestSent, setShareRequestSent] = useState(false);
    const userAccounts = useSelector(selectOfflineReportsUserAccountInfo);
    const [accountMembers, setAccountMembers] = useState<MultiSelectItemProps[]>(
        createAccountMembersItems(
            userAccounts?.members.filter((member) => !sharedWith?.includes(member.id)) || [],
        ),
    );
    const sharingOptions = useMemo(
        () => getShareMenuOptions(userAccounts?.account_name || '', accountShared),
        [accountShared, userAccounts?.account_name],
    );
    const [selectedSharingTarget, setSelectedSharingTarget] = useState<BasicMenuItemProps<string>>(
        sharingOptions.find((option) => option.isSelected) ?? sharingOptions[0],
    );
    const [newComment, setNewComment] = useState('');
    const { setClosedReportSharedDataValue } = useOfflineReportsActions();
    const [isSendingRequest, setIsSendingRequest] = useState<boolean>(false);

    const getUsersEmailsList = useCallback(
        (sharingUsersIdList: string[], target: string) => {
            const emailList: string[] = [];

            if (sharingUsersIdList.length > 0 && target === 'user') {
                userAccounts?.members.forEach((member: { id: string; username: string }) => {
                    if (sharingUsersIdList.includes(member.id)) {
                        emailList.push(member.username);
                    }
                });
            } else if (target === 'account') {
                userAccounts?.members.forEach((member: { id: string; username: string }) => {
                    emailList.push(member.username);
                });
            }
            return emailList;
        },
        [userAccounts],
    );

    const resetUsersSelection = useCallback(() => {
        setAccountMembers((prevItems) =>
            prevItems.map((prevItem) => ({
                ...prevItem,
                isSelected: false,
            })),
        );
    }, []);

    const updateCurrentShareList = (sharedId: string) => {
        const index = currentShareList.indexOf(sharedId);

        if (index !== -1) {
            const tempShareList = [...currentShareList];

            tempShareList.splice(index, 1);
            setCurrentShareList(tempShareList);
            setAccountMembers((prevItems) => [
                ...prevItems,
                ...createAccountMembersItems([{ username: usersMap[sharedId], id: sharedId }]),
            ]);
        }
    };
    const onShareButtonClick = () => {
        if (isShareOptionDisabled) {
            closeSupportRequest();
        } else {
            onShareReport();
        }
    };
    const onShareReport = async () => {
        try {
            setIsSendingRequest(true);
            const sharedWithUsers = [
                ...accountMembers.filter((item) => item.isSelected).map((item) => item.value),
                ...currentShareList,
            ];
            const target = selectedSharingTarget.value;

            await supportApi.shareOfflineReportsComment(id.toString(), {
                target: target,
                users: sharedWithUsers,
                email_message: newComment,
                shared_users_emails: getUsersEmailsList(sharedWithUsers, target),
                report_name: reportName,
            });

            setClosedReportSharedDataValue({
                id,
                target,
                sharedWith: sharedWithUsers,
            });
            setIsSendingRequest(false);
            setShareRequestSent(true);
        } catch (e) {
            closeSupportRequest();
        }
    };
    const isShareOptionDisabled = useMemo(() => {
        if (isSendingRequest) return true;

        if (selectedSharingTarget.value === 'account') {
            if (target !== 'account') {
                return false;
            }
            return true;
        }

        if (!accountMembers.some((item) => item.isSelected)) {
            if (sharedWith && sharedWith.length !== currentShareList.length) {
                return false;
            }
            if (target === 'account') {
                return false;
            }
            return true;
        }
        return false;
    }, [
        isSendingRequest,
        selectedSharingTarget.value,
        accountMembers,
        target,
        sharedWith,
        currentShareList.length,
    ]);
    const accountMembersSelectionClassName = classNames(styles.autoCompleteContainer, {
        [styles.autoCompleteContainerInvisible]: selectedSharingTarget.value === 'account',
    });

    if (shareRequestSent) {
        return (
            <ConfirmationDialogBox
                open={shareRequestSent}
                illustration={'ReportDailyIllustration'}
                title={SHARE_SUCCESS_TITLE}
                content={SHARE_SUCCESS_TEXT}
                actionsOrientation={'horizontal'}
                actions={
                    <>
                        <Button
                            variant={'primary-brand'}
                            fullWidth
                            testId={'share-success-confirm-button'}
                            onClick={() => {
                                setShareRequestSent(false);
                                closeSupportRequest();
                            }}
                        >
                            {SHARE_SUCCESS_BUTTON_TEXT}
                        </Button>
                    </>
                }
            />
        );
    }

    return (
        <>
            <TitleModalHeader title={'Share'} />
            <Modal.Content>
                <div className={styles.modalBody}>
                    <div className={styles.reportHeaderWrapper}>
                        <BodyText variant="bold">{reportName}</BodyText>
                        <BodyText color="secondary">{`Target | ${tableDateFormatter(
                            reportDates.from,
                            false,
                        )} - ${tableDateFormatter(reportDates.to, false)}`}</BodyText>
                    </div>
                    <Divider />
                    <div className={styles.filterTaggedButtonWrapper}>
                        <Dropdown
                            testId={'xtra-request-share-modal-access-dropdown-container'}
                            trigger={
                                <FilterTaggedButton
                                    categoryText="Access: "
                                    Icon={selectedSharingTarget?.LeftComponent}
                                    tagValue={selectedSharingTarget?.label as string}
                                    testId="xtra-share-options-dropdown"
                                    paddingSize={8}
                                    size={'default'}
                                    width={450}
                                />
                            }
                            scaleToAnchor
                            appendToParent
                            closeOnSelection
                        >
                            <BasicDropdownMenu
                                items={sharingOptions}
                                onItemSelection={(item: BasicMenuItemProps<string>) => {
                                    sharingOptions.forEach((option) => {
                                        if (option.value === item.value) {
                                            option.isSelected = true;
                                        } else {
                                            option.isSelected = false;
                                        }
                                    });
                                    setSelectedSharingTarget(item);
                                    resetUsersSelection();
                                }}
                            />
                        </Dropdown>
                    </div>
                    <div className={accountMembersSelectionClassName}>
                        <Dropdown
                            testId={'xtra-request-share-modal-dropdown'}
                            scaleToAnchor
                            appendToParent
                            onInlineSearch={setQuery}
                            trigger={
                                <UnifiedSelectBox
                                    aria-labelledby={'xtra-request-share-modal-select-label'}
                                    testId={'xtra-request-share-modal-select'}
                                    placeholder={'Add users from your account'}
                                    value={
                                        accountMembers
                                            .filter((item) => item.isSelected)
                                            .map((item) => ({
                                                id: item.value,
                                                key: item.value,
                                                label: item.label,
                                                onDelete: () => {
                                                    setAccountMembers((prevItems) =>
                                                        prevItems.map((prevItem) =>
                                                            prevItem.value === item.value
                                                                ? {
                                                                      ...prevItem,
                                                                      isSelected: false,
                                                                  }
                                                                : prevItem,
                                                        ),
                                                    );
                                                },
                                            })) as ChipValueType[]
                                    }
                                />
                            }
                        >
                            <div style={{ minWidth: '220px' }}>
                                <MultiSelectDropdownMenu
                                    items={accountMembers as MultiSelectItemProps[]}
                                    onSelection={setAccountMembers}
                                    searchTerm={query}
                                />
                            </div>
                        </Dropdown>
                    </div>
                    {!isShareOptionDisabled && (
                        <div className={styles.textAreaWrapper}>
                            <TextAreaField
                                value={newComment}
                                onChange={setNewComment}
                                label=""
                                placeholder="Write your message here"
                                initialHeight={104}
                            />
                        </div>
                    )}
                    {currentShareList &&
                        currentShareList[0] !== '' &&
                        !accountMembers.some((member) => member.isSelected) &&
                        selectedSharingTarget.value !== 'account' && (
                            <div className={styles.sharedWithListWrapper}>
                                {currentShareList.map((user) => (
                                    <div className={styles.sharedWithList}>
                                        <div key={user} className={styles.sharedWithItem}>
                                            {usersMap[user]}
                                        </div>
                                        <IconButton
                                            aria-label="remove user"
                                            testId="xtra-sharing-remove-user-button"
                                            Icon={<Icon weight="duotone" type="Trash" />}
                                            variant="ghost"
                                            onClick={() => updateCurrentShareList(user)}
                                        />
                                    </div>
                                ))}
                            </div>
                        )}
                </div>
            </Modal.Content>
            <Modal.Footer>
                <Modal.Footer.RightControls>
                    <Button
                        testId="xtra-share-modal-submit-button"
                        variant="primary-brand"
                        fullWidth
                        onClick={onShareButtonClick}
                        isLoading={isSendingRequest}
                    >
                        {isShareOptionDisabled ? 'Done' : 'Share'}
                    </Button>
                </Modal.Footer.RightControls>
            </Modal.Footer>
        </>
    );
};
